/**
 * Compile a Medium frame's programs before its first frame (lifted from the
 * dungeon-floors prototype's `dungeon-precompile.js`; gotcha
 * "floor-reload-program-lifetime": the old floor is disposed only after the
 * new one's programs exist). The scene is compiled for the lens's linear
 * offscreen target (the variant the frame really uses, not the screen
 * variant), together with the full-screen lens materials and the shadow
 * pass's depth program. Submission is synchronous; where the browser compiles
 * in parallel, the caller bakes the light volume while the GPU process
 * compiles, and the first frame waits for both. `staged` adds programs no
 * object of the scene uses yet (slice 3: the sprite programs, before the first
 * sheet lands), compiled with the scene's lights; `precompileStaged` does the
 * same at Off, for the screen.
 */
import * as THREE from 'three';

const staging = new THREE.Scene();
const plane = new THREE.PlaneGeometry(2, 2);
const postCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
// The shadow pass's depth program (instanced, instance-coloured casters, back faces, RGBA-packed depth): a
// page-lifetime staging caster with the same parameters shares the program three's shadow map will ask for.
const depthStaging = new THREE.Scene();
const caster = new THREE.InstancedMesh(plane, new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, side: THREE.BackSide }), 1);
caster.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(3), 3);
caster.frustumCulled = false;
depthStaging.add(caster);

/** Programs to compile with a scene that none of its objects uses yet: a colour pass's, and a shadow pass's (null at Off). */
export type Staged = { readonly colour: THREE.Scene; readonly depth: THREE.Scene | null };

export const precompile = (
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  target: THREE.WebGLRenderTarget,
  fullscreen: readonly THREE.Material[],
  staged: Staged | null = null,
): void => {
  const previous = renderer.getRenderTarget();
  try {
    renderer.setRenderTarget(target);
    renderer.compile(scene, camera);
    if (staged !== null) renderer.compile(staged.colour, camera, scene);
    if (renderer.shadowMap.enabled) renderer.compile(depthStaging, camera, scene);
    if (renderer.shadowMap.enabled && staged?.depth) renderer.compile(staged.depth, camera, scene);
    for (const material of fullscreen) {
      const mesh = new THREE.Mesh(plane, material);
      mesh.frustumCulled = false;
      staging.add(mesh);
    }
    renderer.setRenderTarget(null);
    renderer.compile(staging, postCamera);
  } finally {
    staging.clear();
    renderer.setRenderTarget(previous);
  }
};

/** Off: compiles staged programs for the screen with the scene's state, so no ordinary frame compiles them. */
export const precompileStaged = (renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, staged: Staged): void => {
  renderer.compile(staged.colour, camera, scene);
};
